import React from "react";
import { cn } from "@/src/lib/utils";

export interface ToggleOption {
  id: string;
  label?: string;
  icon?: React.ComponentType<{ className?: string }>;
  iconColor?: string;
}

export interface ToggleProps {
  options: [ToggleOption, ToggleOption]; // Exactly 2 options
  value: string;
  onChange: (value: string) => void;
  variant?: "primary" | "secondary";
  size?: "sm" | "md" | "lg";
  className?: string;
  disabled?: boolean;
}

export function Toggle({
  options,
  value,
  onChange,
  variant = "primary",
  size = "md",
  className,
  disabled = false,
}: ToggleProps) {
  const [leftOption, rightOption] = options;

  const baseClasses = cn(
    "inline-flex items-center rounded-lg transition-all duration-200",
    {
      // Primary variant colors
      "bg-persian-100": variant === "primary",
      // Secondary variant colors
      "bg-white border border-gray-200": variant === "secondary",
    },
    {
      // Size variations
      "gap-1": size === "sm",
      "gap-1.5": size === "md",
      "gap-2": size === "lg",
    },
    className,
  );

  const getOptionClasses = (
    optionId: string,
    isSelected: boolean,
    hasLabel: boolean,
  ) => {
    return cn(
      "relative font-semibold inline-flex items-center justify-center rounded-md  transition-all duration-200 cursor-pointer select-none",
      {
        // Size classes with label
        "px-3 py-1.5 text-xs gap-1.5": size === "sm" && hasLabel,
        "px-4 py-2 text-sm gap-2": size === "md" && hasLabel,
        "px-5 py-2.5 text-base gap-2.5": size === "lg" && hasLabel,
        // Size classes icon-only (centered, slightly bigger padding)
        "px-2 py-1": size === "sm" && !hasLabel,
        "px-2.5 py-1.5": size === "md" && !hasLabel,
        "px-3 py-2": size === "lg" && !hasLabel,
      },
      {
        // Primary variant selected state
        "bg-persian-950 text-white shadow-sm":
          variant === "primary" && isSelected,
        "text-persian-990 hover:bg-persian-200":
          variant === "primary" && !isSelected,

        // Secondary variant selected state
        "bg-concrete-200 text-concrete-700 shadow-sm":
          variant === "secondary" && isSelected,
        "text-concrete-700 hover:text-concrete-800 hover:bg-concrete-50":
          variant === "secondary" && !isSelected,
      },
      {
        "opacity-50 cursor-not-allowed": disabled,
      },
    );
  };

  const handleOptionClick = (optionId: string) => {
    if (!disabled && optionId !== value) {
      onChange(optionId);
    }
  };

  const renderOption = (option: ToggleOption) => {
    const isSelected = option.id === value;
    const Icon = option.icon;
    const hasLabel = Boolean(option.label);

    return (
      <button
        key={option.id}
        type="button"
        className={getOptionClasses(option.id, isSelected, hasLabel)}
        onClick={() => handleOptionClick(option.id)}
        disabled={disabled}
        aria-pressed={isSelected}
        role="radio"
        aria-checked={isSelected}
        aria-label={option.label || option.id}
      >
        {Icon && (
          <Icon
            className={cn(
              "flex-shrink-0",
              {
                // Icon sizes with label
                "w-3 h-3": size === "sm" && hasLabel,
                "w-4 h-4": size === "md" && hasLabel,
                "w-5 h-5": size === "lg" && hasLabel,
              },
              {
                // Slightly bigger icon sizes when no label
                "w-4 h-4": size === "sm" && !hasLabel,
                "w-5 h-5": size === "md" && !hasLabel,
                "w-6 h-6": size === "lg" && !hasLabel,
              },
              option.iconColor ? option.iconColor : "",
            )}
          />
        )}
        {hasLabel && <span className="font-semibold">{option.label}</span>}
      </button>
    );
  };

  return (
    <div className={baseClasses} role="radiogroup" aria-label="Toggle options">
      {renderOption(leftOption)}
      {renderOption(rightOption)}
    </div>
  );
}
